import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  AccountBalance as CashIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import GirviPageShell from '../common/GirviPageShell';
import baseURL from '../../baseURL';

const CashBalance = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [cashBalance, setCashBalance] = useState(null);
  
  // Form states
  const [openingBalanceForm, setOpeningBalanceForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    financialYear: ''
  });
  
  const [addCashForm, setAddCashForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: 'Cash added to cash in hand'
  });
  
  const [deductCashForm, setDeductCashForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: 'Cash deducted from cash in hand'
  });

  // Fetch cash balance on component mount
  useEffect(() => {
    fetchCashBalance();
  }, []);

  const fetchCashBalance = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/cash-balance`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cash balance');
      }

      const data = await response.json();
      setCashBalance(data.data);
    } catch (error) {
      console.error('Error fetching cash balance:', error);
      setSnackbar({
        open: true,
        message: 'Error loading cash balance',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSetOpeningBalance = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/cash-balance/opening-balance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(openingBalanceForm)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to set opening balance');
      }

      setSnackbar({
        open: true,
        message: data.message || 'Opening balance set successfully',
        severity: 'success'
      });

      setOpeningBalanceForm({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        financialYear: ''
      });

      fetchCashBalance();
    } catch (error) {
      console.error('Error setting opening balance:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Error setting opening balance',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCash = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/cash-balance/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(addCashForm)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to add cash');
      }

      setSnackbar({
        open: true,
        message: data.message || 'Cash added successfully',
        severity: 'success'
      });

      setAddCashForm({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        description: 'Cash added to cash in hand'
      });

      fetchCashBalance();
    } catch (error) {
      console.error('Error adding cash:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Error adding cash',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeductCash = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/cash-balance/deduct`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(deductCashForm)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to deduct cash');
      }

      setSnackbar({
        open: true,
        message: data.message || 'Cash deducted successfully',
        severity: 'success'
      });

      setDeductCashForm({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        description: 'Cash deducted from cash in hand'
      });

      fetchCashBalance();
    } catch (error) {
      console.error('Error deducting cash:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Error deducting cash',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <GirviPageShell
        title="Cash in Hand"
        subtitle="Accounting · Cash"
        chipLabel="Accounting"
        maxWidth="xl"
      >
        {/* Current Balance Card */}
        <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'white' }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Current Cash Balance
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  ₹{loading ? '...' : (cashBalance?.cash?.balance?.toFixed(2) || '0.00')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} sx={{ textAlign: { md: 'right' } }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={fetchCashBalance}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <CashIcon />}
                >
                  Refresh Balance
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab icon={<SettingsIcon />} label="Opening Balance" />
            <Tab icon={<AddIcon />} label="Add Cash" />
            <Tab icon={<RemoveIcon />} label="Deduct Cash" />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        <Paper sx={{ p: 3 }}>
          {/* Opening Balance Tab */}
          {activeTab === 0 && (
            <Box component="form" onSubmit={handleSetOpeningBalance}>
              <Typography variant="h6" gutterBottom>
                Set Opening Cash Balance
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Set the initial cash balance for the financial year. This entry will not appear in daybook or journal.
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    required
                    value={openingBalanceForm.amount}
                    onChange={(e) => setOpeningBalanceForm({ ...openingBalanceForm, amount: e.target.value })}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Date"
                    type="date"
                    required
                    value={openingBalanceForm.date}
                    onChange={(e) => setOpeningBalanceForm({ ...openingBalanceForm, date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Financial Year (Optional)"
                    value={openingBalanceForm.financialYear}
                    onChange={(e) => setOpeningBalanceForm({ ...openingBalanceForm, financialYear: e.target.value })}
                    placeholder="e.g., 2024-25"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={20} /> : <SettingsIcon />}
                  >
                    {submitting ? 'Setting...' : 'Set Opening Balance'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Add Cash Tab */}
          {activeTab === 1 && (
            <Box component="form" onSubmit={handleAddCash}>
              <Typography variant="h6" gutterBottom>
                Add Cash to Cash in Hand
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Add cash to your cash in hand. This entry will not appear in daybook or journal.
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    required
                    value={addCashForm.amount}
                    onChange={(e) => setAddCashForm({ ...addCashForm, amount: e.target.value })}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Date"
                    type="date"
                    required
                    value={addCashForm.date}
                    onChange={(e) => setAddCashForm({ ...addCashForm, date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={addCashForm.description}
                    onChange={(e) => setAddCashForm({ ...addCashForm, description: e.target.value })}
                    placeholder="e.g., Cash deposit from bank"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="success"
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={20} /> : <AddIcon />}
                  >
                    {submitting ? 'Adding...' : 'Add Cash'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Deduct Cash Tab */}
          {activeTab === 2 && (
            <Box component="form" onSubmit={handleDeductCash}>
              <Typography variant="h6" gutterBottom>
                Deduct Cash from Cash in Hand
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Deduct cash from your cash in hand. This entry will not appear in daybook or journal.
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    required
                    value={deductCashForm.amount}
                    onChange={(e) => setDeductCashForm({ ...deductCashForm, amount: e.target.value })}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Date"
                    type="date"
                    required
                    value={deductCashForm.date}
                    onChange={(e) => setDeductCashForm({ ...deductCashForm, date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={deductCashForm.description}
                    onChange={(e) => setDeductCashForm({ ...deductCashForm, description: e.target.value })}
                    placeholder="e.g., Cash withdrawal for expenses"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="error"
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={20} /> : <RemoveIcon />}
                  >
                    {submitting ? 'Deducting...' : 'Deduct Cash'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      </GirviPageShell>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CashBalance;
